// Cliente HTTP para o GPS Drive Backend (Fastify/Prisma próprio, NUNCA o
// Traccar) — usado pelo módulo vehicle-metadata (cadastro real do
// veículo: marca/modelo/ano/rastreador/instalação/imagens) e agora também
// autenticado via JWT próprio do GPS Drive Backend. Base URL vem de
// import.meta.env.VITE_GPSDRIVE_API_URL.
//
// Ao contrário de src/lib/api/client.ts (Traccar), este cliente NUNCA tem
// fallback/modo demo silencioso: se a variável não estiver configurada ou a
// chamada falhar, o erro sempre propaga. É esse comportamento que alimenta
// o relatório de "campos não persistidos" (src/lib/vehicle-cadastro-fields.ts)
// — fingir sucesso aqui esconderia exatamente o que esse mecanismo existe
// para expor.
//
// Sessão do GPS Drive Backend é INDEPENDENTE da sessão do Traccar (ver
// AuthProvider) — accessToken só existe em memória (nunca localStorage/
// sessionStorage, mitiga XSS), recuperado via refresh token HttpOnly a
// cada boot da página. Perder o access token (F5, aba nova) não desloga o
// usuário: o primeiro request 401 (ou a recuperação de sessão no boot)
// dispara um refresh silencioso.

import { fetchWithNetworkRetry } from "./fetch-retry";
import { reportWrite } from "@/lib/live-sync";
import { resolveApiBase } from "./base-url";

export const GPSDRIVE_API_URL: string = resolveApiBase(
  (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_GPSDRIVE_API_URL) || "",
);

// storage.service.ts (backend) devolve imageUrls como caminho relativo
// ("/api/v1/storage/..."), nunca absoluto — se o front jogar isso direto
// num <img src>, resolve contra a própria origem do frontend, não do GPS
// Drive Backend. Toda URL vinda de imageUrls/installImageUrls deve passar
// por aqui antes de virar src de <img>.
export function resolveGpsDriveUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${GPSDRIVE_API_URL}${path}`;
}

export class GpsDriveApiError extends Error {
  status: number;
  // Código do backend (campo "error" da resposta), ex.: WRONG_COMPANY_DOMAIN.
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

// Domínio próprio de empresa (2026-10-17): o backend recusa a sessão de
// quem é de outra empresa (403 WRONG_COMPANY_DOMAIN). O AuthProvider sai do
// rastreamento também e volta pro login com o aviso.
export const WRONG_COMPANY_DOMAIN = "WRONG_COMPANY_DOMAIN";
let onWrongCompanyDomain: ((message: string) => void) | null = null;
export function setWrongCompanyDomainHandler(handler: ((message: string) => void) | null): void {
  onWrongCompanyDomain = handler;
}

// --------------------------------------------------------------------
// Estado da sessão (em memória, módulo singleton — nunca persistido).
// --------------------------------------------------------------------

let accessToken: string | null = null;

// Notificado quando a sessão do GPS Drive Backend expira de vez (refresh
// também falhou) — o AuthProvider usa isso para mostrar "sessão de gestão
// expirada" SEM tocar na sessão do Traccar (mapa/posições/comandos
// continuam vivos, ver relatório da etapa de auth). Diferente de
// setUnauthorizedHandler em lib/api/client.ts (Traccar) de propósito — as
// duas sessões nunca compartilham handler.
let onSessionExpired: (() => void) | null = null;

// "Logar como" (impersonate) emite só um access token pro usuário alvo, de
// propósito SEM criar/rotacionar nenhum refresh token (ver AuthService#
// impersonate no backend — a sessão do administrador nunca é tocada). Isso
// significa que /auth/refresh e /auth/session-refresh, se chamados
// enquanto impersonando, SEMPRE devolvem a identidade do ADMINISTRADOR
// (dono do cookie de refresh e da sessão do Traccar), nunca a do usuário
// impersonado — não existe refresh/sessão "do usuário impersonado" pra
// renovar. Sem esta flag, QUALQUER 401 (token do alvo expirou — TTL curto,
// nunca renova — ou uma rota que o alvo não tem acesso) disparava o
// fallback normal de renovação silenciosa e trocava de volta pro admin SEM
// avisar ninguém: a tela continuava dizendo "Você está logado como X" mas
// as chamadas já eram todas do administrador de novo — bug real relatado
// 2026-08-28 ("logar como não funciona"/reverte sozinho em segundos).
// Enquanto `impersonating` for true, fetchWithAuth nunca tenta renovar
// sozinho; em vez disso avisa onImpersonationExpired, que o AuthProvider
// usa pra encerrar a impersonação de forma visível (restaura o admin e
// avisa o operador), nunca silenciosamente.
let impersonating = false;
let onImpersonationExpired: (() => void) | null = null;

export function setGpsDriveAccessToken(token: string | null): void {
  accessToken = token;
}

export function getGpsDriveAccessToken(): string | null {
  return accessToken;
}

export function setGpsDriveSessionExpiredHandler(handler: (() => void) | null): void {
  onSessionExpired = handler;
}

export function setGpsDriveImpersonating(value: boolean): void {
  impersonating = value;
}

export function isGpsDriveImpersonating(): boolean {
  return impersonating;
}

export function setGpsDriveImpersonationExpiredHandler(handler: (() => void) | null): void {
  onImpersonationExpired = handler;
}

// Renovação antecipada (2026-10-23, "os outros sistemas a pessoa loga de
// manhã e nunca mais precisa sair"): o access token vive 15 min. Em vez de
// esperar uma chamada tomar 401, renova quando faltar pouco — num relógio
// de 1 min e logo que a aba volta a aparecer / o PC acorda / a rede volta.
// Sem token (sessão já expirada) ou durante "Logar como", não faz nada.
const KEEPALIVE_MARGIN_MS = 3 * 60_000;

function accessTokenExpiresAt(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))) as { exp?: number };
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function renewIfExpiring(): void {
  if (!accessToken || impersonating || refreshInFlight) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  const exp = accessTokenExpiresAt(accessToken);
  if (exp !== null && exp - Date.now() > KEEPALIVE_MARGIN_MS) return;
  void refreshGpsDriveSession();
}

export function startGpsDriveKeepAlive(): () => void {
  if (typeof window === "undefined") return () => {};
  const onVisible = () => {
    if (document.visibilityState === "visible") renewIfExpiring();
  };
  const interval = setInterval(renewIfExpiring, 60_000);
  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("online", renewIfExpiring);
  window.addEventListener("focus", renewIfExpiring);
  return () => {
    clearInterval(interval);
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("online", renewIfExpiring);
    window.removeEventListener("focus", renewIfExpiring);
  };
}

function requireBaseUrl(): string {
  if (!GPSDRIVE_API_URL) {
    throw new GpsDriveApiError(
      "VITE_GPSDRIVE_API_URL não configurada — não é possível persistir o cadastro do veículo",
      0,
    );
  }
  return GPSDRIVE_API_URL;
}

// Single-flight: se várias chamadas tomam 401 ao mesmo tempo (ex.: a lista
// da frota dispara N requests em paralelo com o token já vencido), só a
// primeira dispara um POST /auth/refresh de verdade — as demais esperam a
// mesma promise em vez de cada uma tentar renovar por conta própria (o que
// rotacionaria o refresh token várias vezes e derrubaria as sessões
// concorrentes, ver detecção de reuso no backend).
//
// Este é o ÚNICO ponto do app que chama POST /auth/refresh — inclusive a
// recuperação de sessão no boot (gpsdrive-auth.service.ts#recoverSession)
// passa por aqui, nunca faz seu próprio fetch. Antes disso, recoverSession
// tinha um fetch direto separado: no boot, ele podia disparar ao mesmo
// tempo que um 401 de outra chamada (ex.: abrir "Editar Dispositivo" logo
// após o F5) acionava ESTE refreshAccessToken — duas chamadas a
// /auth/refresh com o MESMO cookie ainda não rotacionado, e a que
// chegasse por último no backend apresentava um refresh token que a
// primeira já tinha revogado. O backend trata isso como reuso (possível
// roubo) e revoga a sessão inteira — daí o aviso "sessão de gestão
// expirada" aparecendo sem o usuário ter feito nada de errado (bug real,
// reproduzido pelos logs de produção em 2026-08-26). Unificar as duas
// chamadas neste único single-flight elimina a corrida.
export interface GpsDriveRefreshResult {
  accessToken: string;
  user: unknown;
}

let refreshInFlight: Promise<GpsDriveRefreshResult | null> | null = null;

export async function refreshGpsDriveSession(): Promise<GpsDriveRefreshResult | null> {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function tryEndpoint(path: string): Promise<GpsDriveRefreshResult | null> {
  const res = await fetch(`${requireBaseUrl()}${path}`, {
    method: "POST",
    credentials: "include", // manda o(s) cookie(s) HttpOnly relevantes
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    // 5xx = falha TRANSITÓRIA do lado do servidor (ex.: backend
    // momentaneamente sem conseguir falar com o Traccar/seu próprio
    // Postgres — incidente real em produção 2026-09-01, ~7min de
    // instabilidade sob carga alta). Nunca confundir com um 401/403 limpo
    // (sessão/token REALMENTE inválidos, não adianta tentar de novo) —
    // lança pra performRefresh() decidir tentar de novo, em vez de
    // devolver null e já cair direto em "sessão de gestão expirada".
    if (res.status >= 500) {
      throw new GpsDriveApiError(`Erro ${res.status} ao renovar sessão`, res.status);
    }
    if (res.status === 403) {
      const detail = (await res.json().catch(() => undefined)) as { error?: string; message?: string } | undefined;
      if (detail?.error === WRONG_COMPANY_DOMAIN) onWrongCompanyDomain?.(detail.message ?? "");
    }
    return null;
  }
  const data = (await res.json()) as { accessToken: string; user: unknown };
  accessToken = data.accessToken;
  return { accessToken: data.accessToken, user: data.user };
}

// Sessão de gestão (2026-08-28): antes de declarar a sessão morta e exigir
// e-mail/senha de novo (onSessionExpired), tenta reemitir silenciosamente
// via /auth/session-refresh — prova a identidade só pelo cookie de sessão
// do Traccar, que o navegador já manda de graça (Traccar e este backend
// são a MESMA origem, api.gpsdrive.com.br, ver nginx/conf.d/
// api.gpsdrive.com.br.conf). Cobre o caso comum (refresh token do GPS
// Drive expirado/revogado, Traccar continua logado) sem NUNCA pedir senha
// — só cai no aviso "sessão de gestão expirada" se o Traccar TAMBÉM não
// tiver mais sessão válida, caso em que o AuthGate já está redirecionando
// pra /login de qualquer forma (ver auditoria 2026-08-28).
// Uma tentativa completa (refresh → session-refresh). `transient=true`
// significa "alguma das duas chamadas falhou por rede/5xx" — vale a pena
// tentar de novo. `transient=false` com result=null significa as duas
// devolveram um 401/403 limpo (sessão/token realmente inválidos) — não
// adianta insistir, desiste na hora.
async function attemptRefresh(): Promise<{ result: GpsDriveRefreshResult | null; transient: boolean }> {
  let transient = false;
  try {
    const renewed = await tryEndpoint("/api/v1/auth/refresh");
    if (renewed) return { result: renewed, transient: false };
  } catch {
    transient = true;
  }

  try {
    const recovered = await tryEndpoint("/api/v1/auth/session-refresh");
    if (recovered) return { result: recovered, transient: false };
  } catch {
    transient = true;
  }

  return { result: null, transient };
}

// Sequência de espera entre tentativas quando a falha é transitória (rede/
// 5xx) — nunca quando é um 401/403 limpo, ver attemptRefresh. Cobre o
// incidente real de produção 2026-09-01 (backend momentaneamente sem
// conseguir falar com Traccar/Postgres sob carga alta, ~7min): antes disso,
// UMA falha de rede pontual já derrubava a sessão de gestão e obrigava
// digitar a senha de novo, mesmo com a sessão em si continuando válida.
const REFRESH_RETRY_DELAYS_MS = [1500, 3000];

// true quando a última renovação falhou com 401/403 "limpo" nas duas rotas
// (sessão morta de verdade), false quando foi rede/5xx — ver
// auth-context.tsx: só no 1º caso a aba volta sozinha pro login.
let lastRefreshFailedClean = false;
export function lastRefreshFailureWasClean(): boolean {
  return lastRefreshFailedClean;
}

async function performRefresh(): Promise<GpsDriveRefreshResult | null> {
  for (let attempt = 0; attempt <= REFRESH_RETRY_DELAYS_MS.length; attempt++) {
    const { result, transient } = await attemptRefresh();
    if (result) {
      lastRefreshFailedClean = false;
      return result;
    }
    lastRefreshFailedClean = !transient;
    if (!transient) break;
    if (attempt < REFRESH_RETRY_DELAYS_MS.length) await sleep(REFRESH_RETRY_DELAYS_MS[attempt]);
  }

  accessToken = null;
  onSessionExpired?.();
  return null;
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
}

async function parseErrorMessage(res: Response, path: string): Promise<string> {
  // Mensagem crua do @fastify/rate-limit vem em inglês ("Rate limit exceeded...").
  if (res.status === 429) {
    return "O servidor está recebendo muitas requisições agora. Aguarde alguns segundos e tente salvar de novo.";
  }
  const detail = await res.json().catch(() => undefined);
  return (detail as { message?: string })?.message ?? `Erro ${res.status} ao acessar ${path}`;
}

// Núcleo comum a gpsDriveRequest/gpsDriveUpload: anexa Authorization,
// tenta refresh + repete UMA única vez em caso de 401 (nunca um loop —
// `isRetry` garante que a segunda tentativa nunca dispara um terceiro
// refresh, mesmo que também volte 401).
async function fetchWithAuth(
  path: string,
  init: RequestInit,
  isRetry = false,
): Promise<Response> {
  const baseUrl = requireBaseUrl();
  const headers = new Headers(init.headers);
  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }

  // credentials:"include" de propósito (bug real 2026-08-28): sem isso,
  // fetch() usa o modo padrão "same-origin" — e como o frontend
  // (gpsdrive.com.br) e o GPS Drive Backend (api.gpsdrive.com.br) são
  // origens DIFERENTES (subdomínios distintos contam como origens
  // diferentes pra cookies em fetch), nenhum cookie (nem o refresh token
  // HttpOnly, nem o do Traccar — ver TraccarSessionSwitchService) era
  // mandado nas chamadas normais desta função, só nas que já setavam isso
  // manualmente (login/refresh). O backend já aceita (CORS com
  // credentials:true, ver app.ts) — só faltava o front realmente mandar.
  const res = await fetchWithNetworkRetry(`${baseUrl}${path}`, { ...init, headers, credentials: "include" });

  if (res.status === 401 && !isRetry) {
    if (impersonating) {
      // Nunca chama refresh aqui (ver comentário em setGpsDriveImpersonating
      // acima) — só avisa que a sessão impersonada acabou, pro AuthProvider
      // encerrar a impersonação visivelmente em vez de trocar pro admin em
      // silêncio.
      onImpersonationExpired?.();
      return res;
    }
    const renewed = await refreshGpsDriveSession();
    if (renewed) {
      return fetchWithAuth(path, init, true);
    }
  }

  if (res.ok) reportWrite("gpsdrive", init.method, path);
  return res;
}

// Caminho da própria aba LOGS (activity-log.service.ts) — nunca reportar
// uma falha DELA MESMA aqui, senão viraria um loop (log de erro do log de
// erro...). Comparação por prefixo simples de propósito: é o único path
// que precisa ser excluído.
const ACTIVITY_LOG_PATH = "/api/v1/activity-logs";

// Reporta uma chamada mutante que falhou para a aba LOGS (Editar Usuário)
// — inline (em vez de importar gpsdrive-activity-log.service.ts) só para
// não criar um import circular entre os dois módulos. Fire-and-forget,
// nunca lança.
function reportMutationError(path: string, method: string, status: number, message: string): void {
  if (path === ACTIVITY_LOG_PATH || path.startsWith("/api/v1/live-sync/")) return;
  void gpsDriveRequest(ACTIVITY_LOG_PATH, {
    method: "POST",
    body: { type: "ERROR", message, metadata: { path, method, status } },
  }).catch(() => {});
}

export async function gpsDriveRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, ...rest } = options;

  const res = await fetchWithAuth(path, {
    ...rest,
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(headers ?? {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const message = await parseErrorMessage(res, path);
    // 401 é sessão expirando (tratado por fetchWithAuth/onSessionExpired),
    // não um "erro" do usuário — não polui a aba LOGS.
    if (rest.method && rest.method !== "GET" && res.status !== 401) {
      reportMutationError(path, rest.method, res.status, message);
    }
    throw new GpsDriveApiError(message, res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// Baixa um arquivo autenticado como Blob (fotos de alarme da tela Câmera,
// ver fleet-camera.service.ts) — mesmo padrão de platformFetchBlob em
// gpsdrive-platform-client.ts, mas com a sessão de empresa comum.
export async function gpsDriveFetchBlob(path: string): Promise<Blob> {
  const res = await fetchWithAuth(path, { method: "GET", headers: { Accept: "*/*" } });
  if (!res.ok) {
    throw new GpsDriveApiError(await parseErrorMessage(res, path), res.status);
  }
  return res.blob();
}

// Upload multipart (foto do veículo / foto de instalação) — não passa por
// gpsDriveRequest porque não deve mandar Content-Type manual (o browser
// define o boundary do multipart sozinho a partir do FormData).
export async function gpsDriveUpload<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append("file", file);

  // eslint-disable-next-line no-console -- instrumentação temporária de
  // diagnóstico (upload de foto do veículo), remover depois de achada a
  // causa. Nunca loga conteúdo do arquivo, só metadados/tamanho.
  console.log("[IMAGE] request enviado", { path, size: file.size, type: file.type });
  const res = await fetchWithAuth(path, { method: "POST", body: form });
  // eslint-disable-next-line no-console
  console.log("[IMAGE] resposta recebida — status HTTP", res.status);

  if (!res.ok) {
    throw new GpsDriveApiError(await parseErrorMessage(res, path), res.status);
  }
  const json = (await res.json()) as T;
  // eslint-disable-next-line no-console
  console.log("[IMAGE] imageKey retornado", (json as { imageKey?: unknown } | null)?.imageKey ?? null);
  return json;
}

// Upload multipart com campos extras (ex.: categoria do documento, aba
// DOCUMENTOS) — os campos são anexados ANTES do arquivo de propósito: o
// backend (@fastify/multipart) só consegue ler `file.fields.<nome>` já
// resolvido no momento de request.file() se o campo chegou antes do
// arquivo no multipart stream (ver client-documents.controller.ts).
export async function gpsDriveUploadWithFields<T>(
  path: string,
  file: File,
  fields: Record<string, string>,
): Promise<T> {
  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    form.append(key, value);
  }
  form.append("file", file);

  const res = await fetchWithAuth(path, { method: "POST", body: form });
  if (!res.ok) {
    throw new GpsDriveApiError(await parseErrorMessage(res, path), res.status);
  }
  return (await res.json()) as T;
}

// POST de corpo binário cru (áudio gravado no navegador, ver "Falar com o
// motorista" em routes/camera.tsx) — gpsDriveRequest sempre serializa JSON.
export async function gpsDrivePostBinary<T>(path: string, data: ArrayBuffer): Promise<T> {
  const res = await fetchWithAuth(path, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/octet-stream" },
    body: data,
  });
  if (!res.ok) {
    throw new GpsDriveApiError(await parseErrorMessage(res, path), res.status);
  }
  return (await res.json()) as T;
}

export const gpsDriveGet = <T>(path: string) => gpsDriveRequest<T>(path, { method: "GET" });
export const gpsDrivePost = <T>(path: string, body?: unknown) =>
  gpsDriveRequest<T>(path, { method: "POST", body });
export const gpsDrivePut = <T>(path: string, body?: unknown) =>
  gpsDriveRequest<T>(path, { method: "PUT", body });
export const gpsDrivePatch = <T>(path: string, body?: unknown) =>
  gpsDriveRequest<T>(path, { method: "PATCH", body });
export const gpsDriveDelete = <T>(path: string, body?: unknown) =>
  gpsDriveRequest<T>(path, { method: "DELETE", body });
